/**
 * Тарифы на электроэнергию — разметка 1:1 с legacy admin_snt_tarifs.tpl.
 *
 * Кнопка «Установить тариф» (desktop + mobile-вариант) открывает Bootstrap-3 модал
 * «Установка тарифа»: месяц (от -2 до +11 от текущего), тариф день, тариф ночь, потери.
 * Ниже — таблица установленных тарифов (новые сверху).
 *
 * В legacy-шаблоне НЕТ правки и удаления тарифа (только добавление), поэтому кнопок
 * PUT/DELETE /api/admin/tarifs/{id} на странице нет — вёрстка повторяет шаблон.
 *
 * Техническое отличие: jQuery/bootstrap.js на React-фронте не подключены, поэтому
 * data-toggle="modal" заменён общим компонентом <Modal> (components/Modal.tsx): он даёт
 * закрытие по фону/Esc и блокировку прокрутки. DOM (.modal/.modal-dialog/...), классы
 * и тексты — те же, что в шаблоне.
 *
 * API: GET /api/admin/tarifs, POST /api/admin/tarifs
 *      (month_tarif=Y-m, tarif_day, tarif_n, tarif_loss) — routes/admin_billing.php.
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'

/** Строка snt_tarifs (Invoices::tariffs → SELECT * FROM snt_tarifs ORDER BY month DESC). */
interface TariffRow {
  id: number
  month: string // 'YYYY-MM-DD' — первое число месяца
  tarif_day: string
  tarif_n: string
  loss: string
}

/** Названия месяцев как в Smarty-модификаторе date_format ...:'rus' (modifier.date_format.php). */
const MONTHS_RUS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

/** admin.css/newapproach.css — так же, как их подключает admin.tpl (в index.html их нет). */
function useAdminCss(): void {
  useEffect(() => {
    for (const href of ['/assets/css/admin.css', '/assets/css/newapproach.css']) {
      if (document.head.querySelector(`link[href="${href}"]`) !== null) continue
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }, [])
}

/** Значение <option>: date('Y-m', strtotime("+$offset month")). */
function monthValue(offset: number): string {
  const now = new Date()
  const date = new Date(now.getFullYear(), now.getMonth() + offset, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/** Подпись <option>: {...|date_format:'%m %Y':'':'rus'} → «Январь 2026». */
function monthLabel(value: string): string {
  const [year, month] = value.split('-')
  return `${MONTHS_RUS[Number.parseInt(month, 10) - 1] ?? month} ${year}`
}

/** Ячейка таблицы: {$item.month|date_format:'%d %m %Y':'':'rus'} → «01 Январь 2026». */
function fullDateRus(value: string | null): string {
  if (value === null || value === '') return ''
  const [year, month, day] = value.split(' ')[0].split('-')
  if (!year || !month || !day) return value
  return `${day} ${MONTHS_RUS[Number.parseInt(month, 10) - 1] ?? month} ${year}`
}

/** Список месяцев модала: {for $foo=-2 to 11} (admin_snt_tarifs.tpl:31). */
const MONTH_OPTIONS: string[] = Array.from({ length: 14 }, (_, index) => monthValue(index - 2))

export function AdminTarifsPage() {
  useAdminCss()

  const { data, reload } = useApi<TariffRow[]>('/api/admin/tarifs')
  const tarifs = data ?? []

  const [showModal, setShowModal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [oks, setOks] = useState<string[]>([])

  const [monthTarif, setMonthTarif] = useState(MONTH_OPTIONS[0])
  const [tarifDay, setTarifDay] = useState('')
  const [tarifN, setTarifN] = useState('')
  const [tarifLoss, setTarifLoss] = useState('')

  /** legacy: <input type="submit" name="add_tarif"> → POST и redirect на список. */
  async function addTarif(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setErrors([])
    setOks([])
    try {
      await api.post<{ id: number }>('/api/admin/tarifs', {
        month_tarif: monthTarif,
        tarif_day: tarifDay,
        tarif_n: tarifN,
        tarif_loss: tarifLoss,
      })
      setShowModal(false)
      setTarifDay('')
      setTarifN('')
      setTarifLoss('')
      reload()
    } catch (e: unknown) {
      setErrors([e instanceof ApiError ? e.message : 'Не удалось установить тариф'])
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-xs-6">
          {oks.map((text, index) => (
            <p key={index} className="okke" style={{ float: 'left' }}>
              {text}
            </p>
          ))}
          {errors.map((text, index) => (
            <span key={index} style={{ color: '#ce4844', float: 'left' }}>
              {text}
            </span>
          ))}
        </div>
      </div>
      <div className="row add-form">
        <button
          type="button"
          className="bt hidden-xs"
          style={{ position: 'absolute', top: 20, right: 45 }}
          onClick={() => setShowModal(true)}
        >
          Установить тариф
        </button>
        <button
          type="button"
          className="bt hidden-sm hidden-md hidden-lg xs-right"
          style={{ position: 'absolute', top: 25, right: 5, zoom: '80%' }}
          onClick={() => setShowModal(true)}
        >
          Установить тариф
        </button>

        <div className="col-xs-12">
          <Modal open={showModal} onClose={() => setShowModal(false)} title="Установка тарифа">
            <form method="post" onSubmit={(event) => void addTarif(event)}>
              <table className="table">
                <tbody>
                  <tr>
                    <td>
                      <label>Дата установки тарифа</label>
                    </td>
                    <td>
                      <select
                        name="month_tarif"
                        className="form-control"
                        required
                        value={monthTarif}
                        onChange={(event) => setMonthTarif(event.target.value)}
                      >
                        {MONTH_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {monthLabel(option)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Тариф день</label>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        id="tarif_day"
                        name="tarif_day"
                        className="form-control"
                        value={tarifDay}
                        onChange={(event) => setTarifDay(event.target.value)}
                        placeholder="Тариф День (Пример: 4.12)"
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Тариф ночь</label>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        id="tarif_n"
                        name="tarif_n"
                        className="form-control"
                        value={tarifN}
                        onChange={(event) => setTarifN(event.target.value)}
                        placeholder="Тариф Ночь (Пример: 2.32)"
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Потери</label>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        id="tarif_loss"
                        name="tarif_loss"
                        className="form-control"
                        value={tarifLoss}
                        onChange={(event) => setTarifLoss(event.target.value)}
                        placeholder="Потери в % (Пример: 12.2)"
                        required
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label />
                    </td>
                    <td>
                      <input
                        type="submit"
                        name="add_tarif"
                        className="bt"
                        value="Сохранить"
                        disabled={busy}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </Modal>
        </div>

        <div className="col-xs-12 xs-zoom">
          <table className="table table-hover">
            <thead className="thead-dark">
              <tr>
                <th>№</th>
                <th>Дата установки тарифа</th>
                <th>Тариф день</th>
                <th>Тариф ночь</th>
                <th>Потери %</th>
              </tr>
            </thead>
            <tbody>
              {tarifs.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{fullDateRus(item.month)}</td>
                  <td>{item.tarif_day}</td>
                  <td>{item.tarif_n}</td>
                  <td>{item.loss}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
